import { appStorage } from '../lib/safeStorage';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const FeaturesContext = createContext();

const defaultFeatures = {
  // Enforcing flags — each gates a real page via isFeatureEnabled(...).
  eventBudgets: true,
  clientManagement: true,
  locationManagement: true,
  teamAnnouncements: true,
  staffPortal: true,
  // Maps to the real Vendor Directory / Preferred Vendors feature. Kept for a
  // future gate. OFF by default for DJ Companies; Event Planners always have it.
  vendorDirectory: false,
};

export const FeaturesProvider = ({ children }) => {
  const [features, setFeatures] = useState(() => {
    // Initialize from appStorage immediately
    const savedFeatures = appStorage.getItem('staffed_features');
    if (savedFeatures) {
      return { ...defaultFeatures, ...JSON.parse(savedFeatures) };
    }
    return defaultFeatures;
  });

  // Listen for appStorage changes (from other tabs or direct updates)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'staffed_features' && e.newValue) {
        setFeatures({ ...defaultFeatures, ...JSON.parse(e.newValue) });
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Save features to appStorage whenever they change
  const updateFeatures = useCallback((newFeatures) => {
    setFeatures(newFeatures);
    appStorage.setItem('staffed_features', JSON.stringify(newFeatures));
  }, []);

  const isFeatureEnabled = useCallback((featureKey) => {
    return features[featureKey] !== false;
  }, [features]);

  return (
    <FeaturesContext.Provider value={{ features, updateFeatures, isFeatureEnabled }}>
      {children}
    </FeaturesContext.Provider>
  );
};

export const useFeatures = () => {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
};

export default FeaturesContext;
